import { DetailedProduct } from 'models/DetailedProduct';
import { rm } from 'utils';
import { CartItem } from 'models/CartItem';
import { IWidget, ListViewProduct } from 'models/ListViewProduct';
import { IRelatedProduct } from 'models/BasicProduct';
import { getIsTopTested } from 'components/Shared/Product/selector';
import { getProductTagKey, ProductTagOptionMap } from 'components/Shared/Product';
import { ICart } from 'models/ICart';
import {
  GtmAddToCartLocation,
  GtmCartProduct,
  GtmItemProduct,
  GtmPriceStatus,
  GtmProduct,
  GtmQuotationProduct
} from 'store/googleTagManager/types';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { Price } from 'models/Price';
import { IQuantity } from 'models/IQuantity';
import { MultiQuantity } from 'models/MultiQuantity';
import { mapUrlToCategories } from 'store/googleTagManager/events/utils/shared';
import { CarriageMethod, ProductDetailedMeta, StockStatus, Time } from 'models/ProductMeta';
import { isDetailedMeta, isMediaWidget } from 'views/ProductPage/selector';

const isMultiQuantity = (quantity: IQuantity | MultiQuantity): quantity is MultiQuantity => {
  return (quantity as MultiQuantity).factor !== undefined;
};

type GetGtmPriceStatusProps = {
  isCampaign: boolean;
  isLoweredPrice: boolean;
  isLowPrice: boolean;
};

const getGtmPriceStatus = ({
  isCampaign,
  isLoweredPrice,
  isLowPrice
}: GetGtmPriceStatusProps): GtmPriceStatus => {
  if (isCampaign) {
    return GtmPriceStatus.CAMPAIGN;
  }
  if (isLoweredPrice) {
    return GtmPriceStatus.LOWERED_PRICE;
  }
  if (isLowPrice) {
    return GtmPriceStatus.SEE_PRICE;
  }
  return GtmPriceStatus.NONE;
};

type GetGtmPriceTagProps = Pick<ProductlistViewProduct, 'widgets' | 'isNewProduct'> &
  Partial<Pick<ProductlistViewProduct, 'isTopTested' | 'tacticalFlagMarking'>>;

const getPriceTags = ({
  widgets,
  isNewProduct,
  isTopTested,
  tacticalFlagMarking
}: GetGtmPriceTagProps): string => {
  let resString = '';

  const tagType = getProductTagKey({
    isNewProduct,
    isTopTested: Boolean(getIsTopTested({ widgets })) || isTopTested
  });
  const tagOption = ProductTagOptionMap[tagType];

  if (tagOption && tagOption.text) {
    resString += rm(tagOption.text);
  }

  if (tacticalFlagMarking) {
    resString += '-' + tacticalFlagMarking;
  }

  return resString;
};

type CreateGtmProductProps = Pick<
  DetailedProduct,
  'url' | 'brandName' | 'productId' | 'title' | 'variantName'
> &
  Pick<Price, 'presentationPrice'> &
  Partial<GtmProduct>;

type CreateGtmQuotationProductProps = Pick<DetailedProduct, 'url' | 'brandName' | 'title'>;

export const createGtmQuotationProduct = ({
  url,
  brandName,
  title
}: CreateGtmQuotationProductProps): GtmQuotationProduct => {
  const categoryArray = mapUrlToCategories(url);
  return {
    item_name: title,
    item_brand: brandName,
    item_category: categoryArray.at(0) || '',
    item_category2: categoryArray.at(1) || '',
    item_category3: categoryArray.at(2) || '',
    item_category4: categoryArray.at(3) || '',
    item_category5: categoryArray.at(4) || ''
  };
};

export const createGtmProduct = ({
  url,
  brandName,
  productId,
  title,
  variantName,
  presentationPrice,
  item_web_catalog_id = '',
  item_price_status = GtmPriceStatus.NONE,
  item_price_tag = '',
  item_instock = 0,
  item_review_quantity = 0,
  item_review_score = 0,
  item_delivery_time = Time.NotDefined,
  item_delivery_method = CarriageMethod.NotDefined,
  item_stock_status = StockStatus.NotDefined,
  item_freight_cost = 0,
  item_images = 0,
  item_addons_available = false
}: CreateGtmProductProps): GtmProduct => {
  const categoryArray = mapUrlToCategories(url);
  return {
    item_brand: brandName,
    item_category: categoryArray.at(0) || '',
    item_category2: categoryArray.at(1) || '',
    item_category3: categoryArray.at(2) || '',
    item_category4: categoryArray.at(3) || '',
    item_category5: categoryArray.at(4) || '',
    item_id: productId,
    item_name: title,
    item_variant: variantName,
    price: presentationPrice,
    item_web_catalog_id,
    item_price_status,
    item_price_tag,
    item_instock,
    item_review_quantity,
    item_review_score,
    item_delivery_time,
    item_delivery_method,
    item_stock_status,
    item_freight_cost,
    item_images,
    item_addons_available
  };
};

type CreateGtmItemProductProps = CreateGtmProductProps & {
  itemListName: string;
  webCatalog: string;
  indexInList?: number;
};

const createGtmItemProduct = ({
  url,
  brandName,
  productId,
  title,
  variantName,
  presentationPrice,
  indexInList,
  itemListName,
  webCatalog,
  item_review_score,
  item_instock,
  item_price_status,
  item_price_tag,
  item_delivery_time,
  item_delivery_method,
  item_stock_status,
  item_freight_cost,
  item_images
}: CreateGtmItemProductProps): GtmItemProduct => {
  const gtmProduct = createGtmProduct({
    url,
    presentationPrice,
    brandName,
    title,
    productId,
    variantName,
    item_web_catalog_id: webCatalog,
    item_review_score,
    item_instock,
    item_price_status,
    item_price_tag,
    item_delivery_time,
    item_delivery_method,
    item_stock_status,
    item_freight_cost,
    item_images
  }) as GtmItemProduct;

  gtmProduct.index = indexInList;
  gtmProduct.item_list_name = itemListName;

  return gtmProduct;
};

const getNumberOfProductImages = (widgets: IWidget[] | undefined): number => {
  if (widgets) {
    return widgets.find(isMediaWidget)?.media.length || 0;
  }
  return 0;
};

type MapProductToGtmProps = { addonsAvailable?: boolean } & DetailedProduct;

export const mapProductToGtm = ({
  url,
  brandName,
  productId,
  title,
  variantName,
  price,
  mainWebCatalog,
  reviewSummary,
  reviewAvgScore,
  meta,
  isCampaign,
  isLoweredPrice,
  isLowPrice,
  tacticalFlagMarking,
  isNewProduct,
  widgets,
  isTopTested,
  addonsAvailable = false
}: MapProductToGtmProps): GtmProduct => {
  const avgReviewScore = reviewSummary?.averageScore || reviewAvgScore || 0;
  const { saleStatus, deliveryEstimate, stockStatus } = meta ?? {};
  const detailedMeta = isDetailedMeta(meta) ? (meta as ProductDetailedMeta) : undefined;
  const { carriageMethod, deliveryCostAmount } = detailedMeta ?? {};

  return createGtmProduct({
    presentationPrice: price.presentationPrice,
    url,
    brandName,
    productId,
    title,
    variantName,
    item_web_catalog_id: mainWebCatalog,
    item_review_quantity: reviewSummary?.numberOfReviews || 0,
    item_review_score: avgReviewScore,
    item_instock: saleStatus, // some product are missing, regardless of what types say (e.g. compare products)
    item_price_status: getGtmPriceStatus({
      isCampaign,
      isLoweredPrice,
      isLowPrice
    }),
    item_price_tag: getPriceTags({ widgets, isNewProduct, isTopTested, tacticalFlagMarking }),
    item_delivery_time: deliveryEstimate,
    item_delivery_method: carriageMethod,
    item_stock_status: stockStatus,
    item_freight_cost: deliveryCostAmount,
    item_images: getNumberOfProductImages(widgets),
    item_addons_available: addonsAvailable
  });
};

type MapCartItemToGtmProps = {
  cartItem: CartItem;
  addToCartLocation?: GtmAddToCartLocation;
  itemListName?: string;
  useNewCart?: boolean;
};

export const mapCartItemToGtm = ({
  cartItem,
  addToCartLocation,
  itemListName,
  useNewCart
}: MapCartItemToGtmProps): GtmCartProduct => {
  const { brandName, productId, productUrl, displayName, variantName, unitPrice, quantity } =
    cartItem;

  const product = createGtmProduct({
    url: productUrl,
    presentationPrice: unitPrice,
    brandName,
    productId,
    title: displayName,
    variantName
  });

  const cartProduct = product as GtmCartProduct;

  if (isMultiQuantity(quantity) && !useNewCart && quantity.factor > 0) {
    cartProduct.quantity = quantity.amount * quantity.factor;
  } else {
    cartProduct.quantity = quantity.amount;
  }

  cartProduct.add_to_cart_location = addToCartLocation || '';
  cartProduct.item_list_name = itemListName || '';

  return cartProduct;
};

export const mapCartToGtm = (
  cart: ICart,
  itemListName?: string,
  addItemIndex?: boolean
): GtmCartProduct[] => {
  const items: GtmCartProduct[] = [];

  cart.cartItems.forEach(item => {
    const mainItem = mapCartItemToGtm({ cartItem: item, itemListName });
    const addons =
      cart.subItemRelations[item.cartItemId]?.items.map(addon =>
        mapCartItemToGtm({ cartItem: addon, itemListName })
      ) || [];

    items.push(mainItem);
    items.push(...addons);
  });

  if (addItemIndex) {
    // Gtm wants 1-indexed values, so first item has index 1 not 0
    items.forEach((item, index) => (item.index = index + 1));
  }

  return items;
};

type MapProductToGtmItemProduct<T> = {
  product: T;
  listName: string;
  webCatalog?: string;
  indexInList?: number;
};

export const mapRelatedProductToGtm = ({
  product,
  listName,
  indexInList = 0
}: MapProductToGtmItemProduct<IRelatedProduct>): GtmItemProduct => {
  const { url, price, productId, title, meta } = product;

  // Gtm wants 1-indexed values, so first item has index 1 not 0
  const gtmIndex = indexInList + 1;

  return createGtmItemProduct({
    url,
    presentationPrice: price.presentationPrice,
    productId,
    title,
    brandName: '',
    indexInList: gtmIndex,
    itemListName: listName,
    variantName: '',
    webCatalog: '',
    item_instock: meta?.saleStatus // some product are missing, regardless of what types say (e.g. compare products)
  });
};

export const mapListViewProductToGtm = ({
  product,
  indexInList = 0,
  listName
}: MapProductToGtmItemProduct<ListViewProduct>): GtmItemProduct => {
  const {
    url,
    price,
    brandName,
    productId,
    title,
    reviewAvgScore,
    reviewSummary,
    meta,
    isLowPrice,
    isLoweredPrice,
    isCampaign,
    isNewProduct,
    widgets
  } = product;

  // Gtm wants 1-indexed values, so first item has index 1 not 0
  const gtmIndex = indexInList + 1;
  const avgReviewScore = reviewSummary?.averageScore || reviewAvgScore || 0;
  const { saleStatus, deliveryEstimate, stockStatus } = meta ?? {};
  const detailedMeta = isDetailedMeta(meta) ? (meta as ProductDetailedMeta) : undefined;
  const { carriageMethod, deliveryCostAmount } = detailedMeta ?? {};

  return createGtmItemProduct({
    url,
    presentationPrice: price.presentationPrice,
    productId,
    title,
    brandName,
    indexInList: gtmIndex,
    itemListName: listName,
    variantName: '',
    webCatalog: '',
    item_review_score: avgReviewScore,
    item_review_quantity: reviewSummary?.numberOfReviews || 0,
    item_instock: saleStatus, // some product are missing, regardless of what types say (e.g. compare products)
    item_price_status: getGtmPriceStatus({
      isCampaign,
      isLoweredPrice,
      isLowPrice
    }),
    item_price_tag: getPriceTags({ isNewProduct, widgets }),
    item_delivery_time: deliveryEstimate,
    item_delivery_method: carriageMethod,
    item_stock_status: stockStatus,
    item_freight_cost: deliveryCostAmount,
    item_images: getNumberOfProductImages(widgets)
  });
};

export const mapProductListViewProductToGtm = ({
  product,
  indexInList = 0,
  listName
}: MapProductToGtmItemProduct<ProductlistViewProduct>): GtmItemProduct => {
  const {
    url,
    price,
    brandName,
    productId,
    title,
    mainWebCatalog,
    reviewAvgScore,
    reviewSummary,
    meta,
    isCampaign,
    isLoweredPrice,
    isLowPrice,
    isTopTested,
    widgets,
    isNewProduct,
    tacticalFlagMarking
  } = product;

  // Gtm wants 1-indexed values, so first item has index 1 not 0
  const gtmIndex = indexInList + 1;
  const avgReviewScore = reviewSummary?.averageScore || reviewAvgScore || 0;
  const { saleStatus, deliveryEstimate, stockStatus } = meta ?? {};
  const detailedMeta = isDetailedMeta(meta) ? (meta as ProductDetailedMeta) : undefined;
  const { carriageMethod, deliveryCostAmount } = detailedMeta ?? {};

  return createGtmItemProduct({
    url,
    presentationPrice: price.presentationPrice,
    productId,
    title,
    brandName,
    indexInList: gtmIndex,
    itemListName: listName,
    variantName: '',
    webCatalog: mainWebCatalog,
    item_review_score: avgReviewScore,
    item_review_quantity: reviewSummary?.numberOfReviews || 0,
    item_instock: saleStatus, // some product are missing, regardless of what types say (e.g. compare products)
    item_price_status: getGtmPriceStatus({
      isCampaign,
      isLoweredPrice,
      isLowPrice
    }),
    item_price_tag: getPriceTags({ widgets, isNewProduct, isTopTested, tacticalFlagMarking }),
    item_delivery_time: deliveryEstimate,
    item_delivery_method: carriageMethod,
    item_stock_status: stockStatus,
    item_freight_cost: deliveryCostAmount,
    item_images: getNumberOfProductImages(widgets)
  });
};
